import { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import {
  DEFAULT_MAP_CENTER,
  formatLocationDisplay,
  GOOGLE_MAPS_API_KEY,
  parseCoord,
  placeResultToLocation,
  reverseGeocode,
} from '@/lib/google-maps';
import { useGoogleMapsLoader } from './useGoogleMapsLoader';

const mapContainerStyle = {
  height: '400px',
  width: '100%',
  borderRadius: '20px',
};

const AUTOCOMPLETE_FIELDS = [
  'address_components',
  'geometry',
  'name',
  'formatted_address',
];

export function LocationMapField({
  location,
  locationName = '',
  onLocationChange,
  onLocationNameChange,
  readOnly = false,
}) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const geocodeRequestRef = useRef(0);
  const [isGeocoding, setIsGeocoding] = useState(false);

  const center = {
    lat: parseCoord(location?.latitude, DEFAULT_MAP_CENTER.lat),
    lng: parseCoord(location?.longitude, DEFAULT_MAP_CENTER.lng),
  };

  const hasCoordinates =
    Number.isFinite(Number(location?.latitude)) &&
    Number.isFinite(Number(location?.longitude));

  const setLocationDisplay = useCallback(
    (displayName) => {
      if (inputRef.current) {
        inputRef.current.value = displayName;
      }
      onLocationNameChange?.(displayName);
    },
    [onLocationNameChange],
  );

  const applyLocation = useCallback(
    (nextLocation, displayName) => {
      if (displayName) {
        setLocationDisplay(displayName);
        onLocationChange?.(nextLocation, displayName);
        return;
      }
      onLocationChange?.(nextLocation);
    },
    [onLocationChange, setLocationDisplay],
  );

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place) {
      return;
    }

    const nextLocation = placeResultToLocation(place);
    if (!nextLocation) {
      return;
    }

    const displayName =
      formatLocationDisplay(nextLocation) ||
      place.formatted_address ||
      place.name ||
      '';
    applyLocation(nextLocation, displayName);
  }, [applyLocation]);

  const handleMapClick = useCallback(
    async (event) => {
      if (readOnly || !event.latLng) {
        return;
      }

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const requestId = geocodeRequestRef.current + 1;
      geocodeRequestRef.current = requestId;
      setIsGeocoding(true);
      onLocationChange?.({ latitude: lat, longitude: lng });

      try {
        const resolved = await reverseGeocode(lat, lng);
        if (geocodeRequestRef.current !== requestId) {
          return;
        }
        const displayName = formatLocationDisplay(resolved);
        applyLocation(resolved, displayName);
      } catch {
        if (geocodeRequestRef.current === requestId) {
          onLocationChange?.({ latitude: lat, longitude: lng });
        }
      } finally {
        if (geocodeRequestRef.current === requestId) {
          setIsGeocoding(false);
        }
      }
    },
    [applyLocation, onLocationChange, readOnly],
  );

  useEffect(() => {
    if (inputRef.current && inputRef.current.value !== locationName) {
      inputRef.current.value = locationName;
    }
  }, [locationName]);

  useEffect(() => {
    if (!isLoaded || readOnly || !inputRef.current || !google?.maps?.places) {
      return undefined;
    }

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      fields: AUTOCOMPLETE_FIELDS,
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener(
      'place_changed',
      handlePlaceChanged,
    );

    return () => {
      listener.remove();
      autocompleteRef.current = null;
      google.maps.event.clearInstanceListeners(autocomplete);
    };
  }, [handlePlaceChanged, isLoaded, readOnly]);

  useEffect(() => {
    if (!hasCoordinates || !mapRef.current) {
      return;
    }
    mapRef.current.panTo(center);
  }, [center.lat, center.lng, hasCoordinates]);

  if (loadError) {
    return (
      <div className="location-map-field">
        <div style={{ ...mapContainerStyle, padding: '1rem' }}>
          Map could not be loaded. Check your Google Maps API key and network
          connection.
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="location-map-field">
        <div style={mapContainerStyle} />
      </div>
    );
  }

  if (!GOOGLE_MAPS_API_KEY?.trim()) {
    return (
      <div className="location-map-field">
        <div style={{ ...mapContainerStyle, padding: '1rem' }}>
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in your environment to
          enable the map.
        </div>
      </div>
    );
  }

  return (
    <div className="location-map-field">
      <input
        ref={inputRef}
        type="text"
        defaultValue={locationName}
        placeholder="Enter address or venue name..."
        onChange={(event) => onLocationNameChange?.(event.target.value)}
        disabled={readOnly}
        autoComplete="off"
        aria-busy={isGeocoding}
      />
      <p className="location-map-field-hint">
        {readOnly
          ? 'Location preview.'
          : 'Search for a place, or click the map to set the location.'}
      </p>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={hasCoordinates ? 17 : 12}
        center={center}
        onLoad={(map) => {
          mapRef.current = map;
        }}
        onUnmount={() => {
          mapRef.current = null;
        }}
        onClick={handleMapClick}
        options={{ clickableIcons: !readOnly }}
      >
        {hasCoordinates && <Marker position={center} />}
      </GoogleMap>
    </div>
  );
}
